import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Loader2, ArrowLeft, AlertCircle, BookOpen } from "lucide-react";
import DOMPurify from "dompurify";

import Header from "@/components/Header";
import SocialEmbed from "@/components/SocialEmbed";
import ShareButtons from "@/components/ShareButtons";
import { getBlogArticle } from "@/lib/api";

/**
 * Renders a single blog article. The `body` field arrives from the SEO Hub
 * as HTML (headings, paragraphs, links, lists, `<strong>` etc.) so we
 * sanitise it with DOMPurify and inject it via dangerouslySetInnerHTML.
 * A fallback plain-text renderer kicks in for legacy docs whose body has
 * no HTML tags.
 */
const looksLikeHtml = (s) => /<[a-z][\s\S]*>/i.test(String(s || ""));
export default function BlogPost() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [article, setArticle] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | not_found | error

  // Always start each article from the top · including when the user
  // navigates between two articles without unmounting BlogPost.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    getBlogArticle(slug)
      .then((data) => {
        if (cancelled) return;
        setArticle(data);
        setStatus("ok");
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) setStatus("not_found");
        else setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [slug, i18n.language]);

  const date = article?.published_at
    ? new Date(article.published_at).toLocaleDateString(
        i18n.language === "en" ? "en-US" : "it-IT",
        { day: "numeric", month: "long", year: "numeric" }
      )
    : null;

  // Sanitise the HTML body once per article · DOMPurify strips <script>,
  // inline event handlers, javascript: URLs and other XSS vectors while
  // keeping the formatting tags a blog article needs (headings, lists,
  // links, images, emphasis, blockquote, code, hr).
  //
  // We also STRIP any pre-existing AI-disclaimer sentence baked into legacy
  // article bodies by the CMS · the disclaimer is rendered once, at the
  // bottom of the page, so we must avoid duplicates inside the body.
  // Same treatment for the CMS-baked "Autore: … / Titolo: … / Foto di …"
  // header block: it duplicates (and often orphans, with a broken img) the
  // author card we already render at the bottom of the article.
  const sanitisedBody = useMemo(() => {
    const raw = article?.body || "";
    if (!raw || !looksLikeHtml(raw)) return null;
    let cleaned = String(raw)
      // AI disclaimer variants (IT + EN, old + new)
      .replace(
        /<(p|em|i|small|div)[^>]*>\s*(?:<(?:em|i|small|strong|b)[^>]*>\s*)*(?:Gli articoli e le immagini di questo blog[^<]*|I testi e ogni contenuto di questo blog[^<]*|The articles and images on this blog[^<]*|The texts and all content on this blog[^<]*)\s*(?:<\/(?:em|i|small|strong|b)>\s*)*<\/\1>/gi,
        "",
      )
      // CMS-baked "Autore: …" / "Author: …" row
      .replace(
        /<(p|div)[^>]*>\s*(?:<(?:strong|b|em|i)[^>]*>\s*)*(?:Autore|Author)\s*:\s*(?:<\/(?:strong|b|em|i)>\s*)*[\s\S]*?<\/\1>/gi,
        "",
      )
      // CMS-baked "Titolo: …" / "Title: …" row (author role)
      .replace(
        /<(p|div)[^>]*>\s*(?:<(?:strong|b|em|i)[^>]*>\s*)*(?:Titolo|Title|Ruolo|Role)\s*:\s*(?:<\/(?:strong|b|em|i)>\s*)*[\s\S]*?<\/\1>/gi,
        "",
      )
      // Any "Foto di …" broken/valid image (author photo baked into body)
      .replace(
        /<img[^>]*alt=["'](?:Foto di|Photo of|Photo by)[^"']*["'][^>]*>/gi,
        "",
      );
    return DOMPurify.sanitize(cleaned, {
      ADD_ATTR: ["target", "rel"],
    });
  }, [article?.body, i18n.language]);

  // Build the social-share metadata (Open Graph + Twitter Card). Prefer the
  // article's excerpt; fall back to the first ~200 chars of the plain-text
  // body so the snippet still reads well when previewed on X or Discord.
  const ogMeta = useMemo(() => {
    if (!article) return null;
    const title = article.title || article.slug || "Spectraloom Blog";
    const stripped = (article.body || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    let description = article.excerpt || stripped;
    if (description && description.length > 200) {
      description = description.slice(0, 197).trimEnd() + "…";
    }
    return {
      title: `${title} · Spectraloom Blog`,
      description: description || "Guide, tutorial e casi studio su mix, mastering e produzione musicale.",
      image: article.cover_image || "https://spectraloom.app/icon-512.png",
      url: `https://spectraloom.app/blog/${article.slug}`,
      published_time: article.published_at || undefined,
      locale: i18n.language === "en" ? "en_US" : "it_IT",
    };
  }, [article, i18n.language]);

  return (
    <div className="min-h-screen">
      {ogMeta && (
        <Helmet>
          <title>{ogMeta.title}</title>
          <meta name="description" content={ogMeta.description} />
          <link rel="canonical" href={ogMeta.url} />

          {/* hreflang · Google multilingual guidelines.
              https://developers.google.com/search/docs/specialty/international/localized-versions
              Same slug serves both languages (frontend switches UI via
              `?lang=<code>` on load, see i18n/index.js). Reciprocal
              tags + x-default pointing to the un-parameterised URL
              (which resolves to the visitor's browser language). */}
          <link rel="alternate" hrefLang="it" href={`${ogMeta.url}?lang=it`} />
          <link rel="alternate" hrefLang="en" href={`${ogMeta.url}?lang=en`} />
          <link rel="alternate" hrefLang="x-default" href={ogMeta.url} />

          {/* Open Graph (Facebook, LinkedIn, WhatsApp, Telegram) */}
          <meta property="og:type" content="article" />
          <meta property="og:site_name" content="Spectraloom" />
          <meta property="og:locale" content={ogMeta.locale} />
          <meta property="og:url" content={ogMeta.url} />
          <meta property="og:title" content={ogMeta.title} />
          <meta property="og:description" content={ogMeta.description} />
          <meta property="og:image" content={ogMeta.image} />
          <meta property="og:image:secure_url" content={ogMeta.image} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:image:alt" content={article?.title || "Spectraloom"} />
          {ogMeta.published_time && (
            <meta property="article:published_time" content={ogMeta.published_time} />
          )}
          <meta property="article:author" content={article?.author || "Spectraloom Team"} />
          {article?.category && (
            <meta property="article:section" content={article.category} />
          )}

          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:site" content="@spectraloom" />
          <meta name="twitter:title" content={ogMeta.title} />
          <meta name="twitter:description" content={ogMeta.description} />
          <meta name="twitter:image" content={ogMeta.image} />
          <meta name="twitter:image:alt" content={article?.title || "Spectraloom"} />
        </Helmet>
      )}
      <Header />
      <main
        className="max-w-[860px] mx-auto px-6 lg:px-10 py-12 relative z-10"
        data-testid="blog-post-page"
      >
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-sm text-[color:var(--fg-muted)] hover:text-white mb-6"
          data-testid="blog-back-btn"
        >
          <ArrowLeft size={14} /> {t("blog.back_to_list")}
        </Link>

        {status === "loading" && (
          <div className="flex items-center gap-2 text-[color:var(--fg-muted)] text-sm">
            <Loader2 className="animate-spin" size={16} /> {t("blog.loading")}
          </div>
        )}

        {status === "not_found" && (
          <div
            className="card p-10 text-center"
            data-testid="blog-post-not-found"
          >
            <BookOpen size={28} className="mx-auto mb-3 text-[color:var(--brand)]" />
            <div className="text-white font-semibold mb-2">
              {t("blog.empty_title")}
            </div>
            <p className="text-sm text-[color:var(--fg-secondary)]">
              {t("blog.empty_body")}
            </p>
          </div>
        )}

        {status === "error" && (
          <div
            className="card p-10 text-center border-l-4 border-l-amber-400/50"
            data-testid="blog-post-error"
          >
            <AlertCircle size={28} className="mx-auto mb-3 text-amber-300" />
            <div className="text-white font-semibold mb-2">
              {t("blog.unavailable_title")}
            </div>
            <p className="text-sm text-[color:var(--fg-secondary)]">
              {t("blog.unavailable_body")}
            </p>
          </div>
        )}

        {status === "ok" && article && (
          <article className="fade-up" data-testid="blog-post-article">
            <div className="tiny-caps text-[color:var(--brand)] mb-3">
              {t(`blog.categories.${article.category}`)}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.1] tracking-tight text-white mb-4">
              {article.title || article.slug}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-xs text-[color:var(--fg-muted)] mb-6">
              <span data-testid="blog-post-author">
                {t("blog.author")}: {article.author || "Spectraloom Team"}
              </span>
              {date && (
                <>
                  <span>·</span>
                  <span className="metric-value" data-testid="blog-post-date">
                    {t("blog.published_on")} {date}
                  </span>
                </>
              )}
            </div>

            <div className="mb-8">
              <ShareButtons title={article.title || article.slug} slug={article.slug} />
            </div>

            {article.cover_image && (
              <div className="rounded-xl overflow-hidden border border-white/10 mb-8">
                <img
                  src={article.cover_image}
                  alt=""
                  className="w-full h-auto"
                  draggable={false}
                />
              </div>
            )}

            {article.excerpt && (
              <p
                className="text-lg text-[color:var(--fg-secondary)] leading-relaxed mb-8 border-l-2 border-[color:var(--brand)] pl-4"
                data-testid="blog-post-excerpt"
              >
                {article.excerpt}
              </p>
            )}

            <SocialEmbed embed={article.embed} />

            {sanitisedBody ? (
              <div
                className="blog-body prose prose-invert max-w-none text-[color:var(--fg-secondary)] leading-relaxed"
                data-testid="blog-post-body"
                dangerouslySetInnerHTML={{ __html: sanitisedBody }}
              />
            ) : (
              <div
                className="prose prose-invert max-w-none text-[color:var(--fg-secondary)] leading-relaxed"
                data-testid="blog-post-body"
              >
                {String(article.body || "")
                  .split(/\n{2,}/)
                  .filter((p) => p.trim().length > 0)
                  .map((p, i) => (
                    <p key={i} className="mb-4 whitespace-pre-wrap">
                      {p}
                    </p>
                  ))}
              </div>
            )}

            {(() => {
              const hasName = Boolean(article.author_name);
              const displayName = hasName ? article.author_name : "Spectraloom Staff";
              const displayPhoto = hasName
                ? article.author_photo_url
                : "/icon-512.png";
              const displayTitle = hasName ? article.author_title : "";
              return (
                <div
                  data-testid="article-signature"
                  className="mt-12 pt-6 border-t border-white/10 flex items-center gap-3"
                >
                  {displayPhoto && (
                    <img
                      src={displayPhoto}
                      alt={displayName}
                      width={44}
                      height={44}
                      className={
                        hasName
                          ? "w-11 h-11 rounded-full object-cover shrink-0 border border-white/10 bg-black"
                          : "w-11 h-11 rounded-xl object-cover shrink-0 border border-white/10"
                      }
                      draggable={false}
                    />
                  )}
                  <div className="leading-tight">
                    <p className="text-white font-semibold m-0">{displayName}</p>
                    {displayTitle && (
                      <p className="text-xs text-[color:var(--fg-muted)] m-0 mt-0.5">
                        {displayTitle}
                      </p>
                    )}
                  </div>
                </div>
              );
            })()}

            <p
              data-testid="blog-ai-disclaimer"
              className="mt-6 italic text-center"
              style={{ fontSize: "0.8em", opacity: 0.55 }}
            >
              {t("blog.ai_disclaimer")}
            </p>

            <div className="mt-10 flex justify-center">
              <Link
                to="/blog"
                data-testid="blog-back-btn-bottom"
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/25 text-[color:var(--fg-secondary)] hover:text-white transition-colors px-5 py-2.5 text-sm font-medium no-underline"
              >
                <ArrowLeft size={16} />
                {t("blog.back_to_list_bottom")}
              </Link>
            </div>
          </article>
        )}
      </main>
    </div>
  );
}
